import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = logged out, object = logged in
    const [permissions, setPermissions] = useState({});
    const [error, setError] = useState("");

    const loadPermissions = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/permissions");
            setPermissions(data.permissions || {});
        } catch {
            setPermissions({});
        }
    }, []);

    const [branding, setBranding] = useState(null);
    const loadBranding = useCallback(async () => {
        try {
            const { data } = await api.get("/company/branding");
            setBranding(data);
        } catch {
            setBranding(null);
        }
    }, []);

    const [opsAlerts, setOpsAlerts] = useState(null);
    const loadOpsAlerts = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ops-alerts");
            setOpsAlerts(data);
        } catch {
            setOpsAlerts(null);
        }
    }, []);

    const [features, setFeatures] = useState({ paid_ai_enabled: false });
    useEffect(() => {
        api.get("/features").then((r) => setFeatures(r.data || {})).catch(() => {});
    }, []);

    const loadUser = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            await Promise.all([loadPermissions(), loadBranding()]);
            if (data.role === "super_admin") loadOpsAlerts();
        } catch {
            setUser(false);
            setPermissions({});
            setBranding(null);
            setOpsAlerts(null);
        }
    }, [loadPermissions, loadBranding, loadOpsAlerts]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    // Poll ops alerts every 60s while super_admin is logged in
    useEffect(() => {
        if (user?.role !== "super_admin") return;
        const id = setInterval(loadOpsAlerts, 60_000);
        return () => clearInterval(id);
    }, [user, loadOpsAlerts]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.access_token) setAuthToken(data.access_token);
            setUser(data.user);
            await Promise.all([loadPermissions(), loadBranding()]);
            if (data.user?.role === "super_admin") loadOpsAlerts();
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const register = async (payload) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.access_token) setAuthToken(data.access_token);
            setUser(data.user);
            await Promise.all([loadPermissions(), loadBranding()]);
            return true;
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
            return false;
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        setAuthToken(null);
        setUser(false);
        setPermissions({});
        setBranding(null);
        setOpsAlerts(null);
    };

    // Helper to check a permission. Defaults to false if unknown.
    const can = (perm) => !!permissions[perm];

    return (
        <AuthContext.Provider value={{ user, setUser, login, register, logout, error, setError, loadUser, permissions, can, branding, loadBranding, opsAlerts, loadOpsAlerts, features }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
